import type { MouseEvent as ReactMouseEvent, ReactNode } from 'react';
import Col from 'react-bootstrap/Col';
import { PolarAngleAxis, RadialBar, RadialBarChart, ResponsiveContainer } from 'recharts';
import { Button } from 'modules/core/components/buttons';
import { CHART_RING_TRACK_FILL } from './chartTheme';

export interface KpiCardProps {
  title: string,
  // ⓘ рядом с заголовком — точное определение показателя для бизнеса;
  // карточка только размещает его, содержимое (тултип) приносит отчёт
  titleHint?: ReactNode,
  // Строка — крупным жирным (h4-обёртка ниже). Составное значение (два числа
  // одной карточки) рендерится без обёртки и приносит свою типографику: внутри
  // h4 мелкая подпись цифры наследовала бы крупный кегль.
  value: ReactNode,
  valueClassName?: string,
  visual?: ReactNode, // визуал слева от текста (кольцо явки)
  caption?: string, // мелкая расшифровка под значением
  // Блок справа от текста, прижат к верху карточки (align-self-start): его
  // первая строка встаёт на одну линию с заголовком. В плитке «Явка» отчёта
  // Совкомбанка несёт «Создал» — кольцо долей источников записи и легенду
  // (см. KpiRow в sovkombank).
  aside?: ReactNode,
  onClick?: () => void, // клик по карточке — детализация (список записей)
}

// Карточка KPI — общий кирпич KPI-строки любого отчёта (вид этапа 1 редизайна,
// план, раздел 12): заливка bg-light вместо рамки (карточки стоят на белой
// панели и рамка смотрелась жёстко), скругление крупнее панельного, подпись —
// мелким капсом, значение — жирным. Этап 4 добавил опциональные визуал слева
// (кольцо явки) и расшифровку под значением; блок «Создал» плитки «Явка» —
// aside справа, прижатый к верху. Карточки без опций рендерятся как раньше.
// Состав карточек — у каждого отчёта свой (см. KpiRow в sovkombank).
const KpiCard = ({
  title,
  titleHint,
  value,
  valueClassName = '',
  visual,
  caption,
  aside,
  onClick,
}: KpiCardProps) => {
  const content = (
    <>
      { visual }
      <div className={ visual ? 'ml-3' : undefined }>
        <div className='text-muted small text-uppercase font-weight-bold'>
          { title }
          { /* Клик по ⓘ не открывает детализацию карточки — только тултип */ }
          { titleHint && (
            <span
              className='ml-1'
              onClick={ (event) => event.stopPropagation() }
            >
              { titleHint }
            </span>
          ) }
        </div>
        { typeof value === 'string'
          ? (
            <div className={ `h4 mb-0 mt-1 font-weight-bold ${ valueClassName }`.trim() }>{ value }</div>
          )
          : <div className='mt-1'>{ value }</div> }
        { caption && <div className='small text-muted mt-1'>{ caption }</div> }
      </div>
      { aside && <div className='ml-3 align-self-start'>{ aside }</div> }
    </>
  );

  // Кликабельная карточка — core Button с variant='light': фон тот же, что у
  // bg-light, а hover (лёгкое затемнение) и курсор даёт сам Bootstrap — ни
  // одного нового CSS-класса. Утилиты (p-3, d-flex, text-wrap) идут в сборке
  // после .btn и перекрывают её паддинг, display и white-space: nowrap;
  // shadow-none убирает стандартное кольцо :focus у .btn — оно выступало за
  // края карточки. Фокус снимаем прямо в клике: модалка детализации заберёт
  // фокус себе, а после её закрытия браузер вернул бы фокус на кнопку — и
  // карточка зависала бы «нажатой» серой до первого клика мимо.
  if (onClick) {
    return (
      <Col xs={ 6 } md={ 4 } xl={ 3 }>
        <Button
          variant='light'
          className='w-100 h-100 d-flex align-items-center text-left text-wrap p-3 rounded-lg border-0 shadow-none'
          onClick={ (event: ReactMouseEvent<HTMLButtonElement>) => {
            event.currentTarget.blur();
            onClick();
          } }
        >
          { content }
        </Button>
      </Col>
    );
  }

  return (
    <Col xs={ 6 } md={ 4 } xl={ 3 }>
      <div className='bg-light rounded-lg p-3 h-100 d-flex align-items-center'>{ content }</div>
    </Col>
  );
};

// Кольцо явки (этап 4): дуга — явка цветом светофора, дорожка — $gray-300.
// Размер фиксирован в пикселях (числовые пропы ResponsiveContainer), высота
// карточки не прыгает при обновлении данных; процент и «N из M» — текстом правее
// кольца, поэтому внутри SVG подписей нет. Полярная шкала 0…1: явка — доля,
// тики скрыты — ось нужна только для геометрии дуги.
const ATTENDANCE_RING_SIZE = 64;

interface AttendanceRingProps {
  rate: number | null,
  color: string,
}

const AttendanceRing = ({ rate, color }: AttendanceRingProps) => (
  <ResponsiveContainer width={ ATTENDANCE_RING_SIZE } height={ ATTENDANCE_RING_SIZE }>
    <RadialBarChart
      data={ [{ rate: rate ?? 0 }] }
      startAngle={ 90 }
      endAngle={ -270 }
      innerRadius='70%'
      outerRadius='100%'
      margin={ { top: 0, right: 0, bottom: 0, left: 0 } }
    >
      <PolarAngleAxis type='number' domain={ [0, 1] } tick={ false } />
      <RadialBar
        dataKey='rate'
        fill={ color }
        cornerRadius={ 6 }
        background={ { fill: CHART_RING_TRACK_FILL } }
      />
    </RadialBarChart>
  </ResponsiveContainer>
);

export default KpiCard;
export { AttendanceRing, ATTENDANCE_RING_SIZE };
